import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type PropsWithChildren,
} from "react";
import { useAuth } from "../auth/AuthProvider";
import { supabase } from "../lib/supabase";

export type ModuleKey =
  | "crm"
  | "catalog"
  | "quotations"
  | "workshop"
  | "inventory"
  | "sales"
  | "purchases"
  | "accounting";

export type OrganizationOption = {
  organizationId: string;
  name: string;
  legalName: string | null;
  taxId: string | null;
  role: string;
  isPlatformAdmin: boolean;
};

type OrganizationContextValue = {
  organizations: OrganizationOption[];
  selectedOrganization: OrganizationOption | null;
  selectedOrganizationId: string | null;
  loading: boolean;
  error: string | null;
  selectOrganization: (organizationId: string) => void;
  refreshOrganizations: () => Promise<void>;
  enabledModules: ModuleKey[];
  modulesLoading: boolean;
  isModuleEnabled: (moduleKey: ModuleKey) => boolean;
  refreshModules: () => Promise<void>;
};

const OrganizationContext = createContext<OrganizationContextValue | null>(null);

function storageKey(userId: string) {
  return `core:selected-organization:${userId}`;
}

export function OrganizationProvider({ children }: PropsWithChildren) {
  const { user } = useAuth();
  const [organizations, setOrganizations] = useState<OrganizationOption[]>([]);
  const [selectedOrganizationId, setSelectedOrganizationId] = useState<string | null>(null);
  const [loading, setLoading] = useState(Boolean(user));
  const [error, setError] = useState<string | null>(null);
  const [enabledModules, setEnabledModules] = useState<ModuleKey[]>([]);
  const [modulesLoading, setModulesLoading] = useState(false);

  const refreshOrganizations = useCallback(async () => {
    if (!user) {
      setOrganizations([]);
      setSelectedOrganizationId(null);
      setLoading(false);
      setError(null);
      return;
    }

    setLoading(true);
    setError(null);

    const { data, error: rpcError } = await supabase.rpc(
      "get_current_user_organizations",
    );

    if (rpcError) {
      setOrganizations([]);
      setSelectedOrganizationId(null);
      setError(rpcError.message);
      setLoading(false);
      return;
    }

    const rows = (data ?? []) as Array<{
      organization_id: string;
      organization_name: string;
      legal_name: string | null;
      tax_id: string | null;
      member_role: string | null;
      is_platform_admin: boolean;
    }>;

    const nextOrganizations = rows.map((row) => ({
      organizationId: row.organization_id,
      name: row.organization_name,
      legalName: row.legal_name,
      taxId: row.tax_id,
      role: row.member_role ?? "member",
      isPlatformAdmin: Boolean(row.is_platform_admin),
    }));

    setOrganizations(nextOrganizations);

    const savedId = localStorage.getItem(storageKey(user.id));
    const savedIsAvailable = nextOrganizations.some(
      (organization) => organization.organizationId === savedId,
    );
    const nextId = savedIsAvailable
      ? savedId
      : nextOrganizations[0]?.organizationId ?? null;

    setSelectedOrganizationId(nextId);

    if (nextId) {
      localStorage.setItem(storageKey(user.id), nextId);
    } else {
      localStorage.removeItem(storageKey(user.id));
    }

    setLoading(false);
  }, [user]);

  useEffect(() => {
    void refreshOrganizations();
  }, [refreshOrganizations]);

  const refreshModules = useCallback(async () => {
    if (!selectedOrganizationId) {
      setEnabledModules([]);
      setModulesLoading(false);
      return;
    }

    setModulesLoading(true);

    const { data, error: moduleError } = await supabase.rpc(
      "list_organization_modules_ui",
      { p_organization_id: selectedOrganizationId },
    );

    if (moduleError) {
      setEnabledModules([]);
      setError(moduleError.message);
      setModulesLoading(false);
      return;
    }

    const rows = (data ?? []) as Array<{
      module_key: ModuleKey;
      enabled: boolean;
    }>;

    setEnabledModules(
      rows
        .filter((row) => row.enabled)
        .map((row) => row.module_key),
    );
    setModulesLoading(false);
  }, [selectedOrganizationId]);

  useEffect(() => {
    void refreshModules();
  }, [refreshModules]);

  const selectedOrganization = useMemo(
    () =>
      organizations.find(
        (organization) => organization.organizationId === selectedOrganizationId,
      ) ?? null,
    [organizations, selectedOrganizationId],
  );

  const selectOrganization = useCallback(
    (organizationId: string) => {
      if (!user) return;

      const exists = organizations.some(
        (organization) => organization.organizationId === organizationId,
      );

      if (!exists) return;

      setSelectedOrganizationId(organizationId);
      localStorage.setItem(storageKey(user.id), organizationId);
    },
    [organizations, user],
  );

  const isModuleEnabled = useCallback(
    (moduleKey: ModuleKey) => enabledModules.includes(moduleKey),
    [enabledModules],
  );

  const value = useMemo<OrganizationContextValue>(
    () => ({
      organizations,
      selectedOrganization,
      selectedOrganizationId,
      loading,
      error,
      selectOrganization,
      refreshOrganizations,
      enabledModules,
      modulesLoading,
      isModuleEnabled,
      refreshModules,
    }),
    [
      organizations,
      selectedOrganization,
      selectedOrganizationId,
      loading,
      error,
      selectOrganization,
      refreshOrganizations,
      enabledModules,
      modulesLoading,
      isModuleEnabled,
      refreshModules,
    ],
  );

  return (
    <OrganizationContext.Provider value={value}>
      {children}
    </OrganizationContext.Provider>
  );
}

export function useOrganization() {
  const context = useContext(OrganizationContext);

  if (!context) {
    throw new Error("useOrganization must be used inside OrganizationProvider");
  }

  return context;
}
